/**
 * Reads a cookie value by name
 * @param name - Cookie name to read
 * @param _document Internal parameter for Dependency Injection [DO NOT USE] - document object
 * @returns Cookie value if found, undefined otherwise
 */
export const getCookie = (
  name: string,
  /* istanbul ignore next */
  _document = document,
): string | undefined => {
  if (!_document?.cookie) {
    return undefined;
  }

  const allCookies = _document.cookie.split(';');

  for (const cookie of allCookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name && cookieValue) {
      return decodeURIComponent(cookieValue);
    }
  }

  return undefined;
};

const SEGMENT_ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';
const SEGMENT_USER_ID_COOKIE = 'ajs_user_id';

/**
 * Gets the Segment anonymousId from the root domain cookie if it exists
 * Segment stores anonymousId in a cookie named 'ajs_anonymous_id'
 * @param _getCookie Internal parameter for Dependency Injection [DO NOT USE] - function to get cookie
 * @returns anonymousId from root domain cookie, or undefined if not found
 */
export const getSegmentAnonymousIdFromCookie = (
  /* istanbul ignore next */
  _getCookie = getCookie,
): string | undefined => {
  return _getCookie(SEGMENT_ANONYMOUS_ID_COOKIE);
};

/**
 * Gets the Segment userId from the root domain cookie if it exists.
 * Segment stores the identified user id in a cookie named 'ajs_user_id'
 * @param _getCookie Internal parameter for Dependency Injection [DO NOT USE] - function to get cookie
 * @returns userId from root domain cookie, or undefined if not found
 */
export const getSegmentUserIdFromCookie = (
  /* istanbul ignore next */
  _getCookie = getCookie,
): string | undefined => {
  return _getCookie(SEGMENT_USER_ID_COOKIE);
};

/**
 * Returns whether the current browser session has already been identified to Segment.
 * @param _getSegmentUserIdFromCookie Internal parameter for Dependency Injection [DO NOT USE] - function to get Segment user id
 * @returns true when the Segment user id cookie exists
 */
export const hasSegmentIdentifiedSession = (
  /* istanbul ignore next */
  _getSegmentUserIdFromCookie = getSegmentUserIdFromCookie,
): boolean => {
  return _getSegmentUserIdFromCookie() !== undefined;
};

/**
 * Segment may store the `ajs_anonymous_id` cookie value wrapped in quotes.
 * Strip them so consumers always receive the raw id (or undefined when empty).
 */
const unwrapQuotedCookieValue = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    const inner = trimmed.slice(1, -1).trim();
    return inner || undefined;
  }
  return trimmed;
};

/**
 * Returns the host's Segment anonymousId.
 *
 * Prefers the live Segment analytics client and falls back to the
 * `ajs_anonymous_id` cookie, which Segment may write before the analytics
 * client's `user().anonymousId()` is available. Returns undefined when neither
 * source has a value yet (e.g. before Segment loads or during SSR).
 */
export const getSegmentAnonymousId = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const { analytics } = window;
  const fromAnalytics = analytics?.user?.()?.anonymousId?.();
  if (typeof fromAnalytics === 'string' && fromAnalytics.trim() !== '') {
    return fromAnalytics.trim();
  }

  const fromCookie = getSegmentAnonymousIdFromCookie();
  return fromCookie ? unwrapQuotedCookieValue(fromCookie) : undefined;
};
